import { useState, useEffect, useRef } from 'react';
import { Bell, Check } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NotificationBell — global topbar bell for chat notifications.
 *
 * Polls `/api/clubs/notifications/me` every 30s for the unread count and
 * displays a dropdown with the latest 30 entries when clicked.
 *
 * Token retrieval is role-aware: managers/coaches use `team_token`,
 * club_admins use `club_token`, parents use `parent_token`.
 */
const NotificationBell = ({ tokenKey = 'team_token', onItemClick }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const fetchNotifs = async () => {
    try {
      const token = localStorage.getItem(tokenKey);
      if (!token) return;
      const res = await fetch(`${API}/clubs/notifications/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setItems(d.items || []);
        setUnread(d.unread_count || 0);
      }
    } catch (e) {
      // silent — bell is best-effort
    }
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, [tokenKey]); // eslint-disable-line

  useEffect(() => {
    const handleClick = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem(tokenKey);
      await fetch(`${API}/clubs/notifications/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifs();
    } catch (e) {}
  };

  const handleItemClick = async (item) => {
    try {
      const token = localStorage.getItem(tokenKey);
      await fetch(`${API}/clubs/notifications/${item.id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {}
    setOpen(false);
    fetchNotifs();
    if (onItemClick) onItemClick(item);
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="notification-bell">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 text-muted-foreground hover:text-foreground transition"
        title="Notifications"
        data-testid="notification-bell-btn"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
            data-testid="notification-bell-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-sm shadow-2xl z-50 overflow-hidden"
          data-testid="notification-bell-dropdown"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="font-medium text-sm">Notifications</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] text-primary hover:underline flex items-center gap-1"
                data-testid="notification-mark-all-read"
              >
                <Check size={11} /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                <Bell className="mx-auto mb-2 opacity-40" size={20} />
                <p>You're all caught up</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-border/40 hover:bg-muted/40 transition ${
                    !n.read ? 'bg-primary/5' : ''
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                      {(n.counterparty_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-sm truncate">{n.counterparty_name}</p>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.preview}</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">
                        {n.created_at ? format(parseISO(n.created_at), 'MMM d, HH:mm') : ''}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
